import { useState, useEffect } from 'react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, Area
} from 'recharts';
import { getCandles } from './finnhub';
import { processCandles } from './indicators';

const mono = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 };

const RANGE_OPTIONS = [
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '6M', days: 180 },
  { label: '1Y', days: 365 },
];

const fmt$ = (v) => v == null ? '' : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtVol = (v) => { if (v == null) return ''; if (v >= 1e6) return (v/1e6).toFixed(1)+'M'; if (v >= 1e3) return (v/1e3).toFixed(0)+'K'; return String(v); };

const PriceTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', fontSize: 11, color: '#e2e8f0', ...mono, boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
      <div style={{ fontWeight: 700, marginBottom: 4, color: '#fff' }}>{d.date}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' }}>
        <span style={{ color: '#94a3b8' }}>O</span><span>{fmt$(d.open)}</span>
        <span style={{ color: '#94a3b8' }}>H</span><span>{fmt$(d.high)}</span>
        <span style={{ color: '#94a3b8' }}>L</span><span>{fmt$(d.low)}</span>
        <span style={{ color: '#94a3b8' }}>C</span><span style={{ color: d.close >= d.open ? '#4ade80' : '#f87171', fontWeight: 700 }}>{fmt$(d.close)}</span>
        <span style={{ color: '#94a3b8' }}>Vol</span><span>{fmtVol(d.volume)}</span>
      </div>
      {d.sma14 && <div style={{ marginTop: 4, color: '#60a5fa' }}>SMA 14: {fmt$(d.sma14)}</div>}
      {d.sma50 && <div style={{ color: '#f59e0b' }}>SMA 50: {fmt$(d.sma50)}</div>}
      {d.sma200 && <div style={{ color: '#a78bfa' }}>SMA 200: {fmt$(d.sma200)}</div>}
    </div>
  );
};

const RSITooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d || d.rsi == null) return null;
  const color = d.rsi >= 70 ? '#f87171' : d.rsi <= 30 ? '#4ade80' : '#e2e8f0';
  const label = d.rsi >= 70 ? 'Overbought' : d.rsi <= 30 ? 'Oversold' : 'Neutral';
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '8px 12px', fontSize: 11, color, ...mono, boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
      <div>{d.date}</div>
      <div style={{ fontWeight: 700 }}>RSI: {d.rsi.toFixed(1)} — {label}</div>
    </div>
  );
};

export default function TickerDetail({ ticker, strategy, onClose }) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('1Y');
  const [smaVisible, setSmaVisible] = useState({ sma14: true, sma50: true, sma200: true });

  const strategyColor = {
    'Boring': '#3B82F6', 'Core Growth': '#F59E0B', 'Crypto': '#EC4899',
    'Gold': '#EAB308', 'Robbins': '#14B8A6'
  }[strategy] || '#3B82F6';

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      setError(null);

      // Always fetch 1 year for SMA 200 calculation, then filter display range
      const to = Math.floor(Date.now() / 1000);
      const from = to - (400 * 24 * 60 * 60); // ~13 months to ensure 200+ data points

      const candles = await getCandles(ticker, 'D', from, to);
      if (cancelled) return;

      if (!candles) {
        setError('No data available for this ticker');
        setLoading(false);
        return;
      }

      const processed = processCandles(candles);
      setChartData(processed);
      setLoading(false);
    }
    fetchData();
    return () => { cancelled = true; };
  }, [ticker]);

  // Filter data by selected range for display
  const displayData = (() => {
    const days = RANGE_OPTIONS.find(r => r.label === range)?.days || 365;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return chartData.filter(d => d.dateRaw >= cutoff);
  })();

  // Latest values for the header badges
  const latest = displayData.length > 0 ? displayData[displayData.length - 1] : null;

  const toggleSMA = (key) => setSmaVisible(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading) {
    return (
      <tr>
        <td colSpan={8}>
          <div style={{ padding: '40px 20px', textAlign: 'center', background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
            <div style={{ ...mono, color: '#64748b', fontSize: 12 }}>Loading chart data for {ticker}...</div>
          </div>
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr>
        <td colSpan={8}>
          <div style={{ padding: '30px 20px', textAlign: 'center', background: '#fef2f2', borderBottom: '2px solid #e2e8f0' }}>
            <div style={{ ...mono, color: '#dc2626', fontSize: 12 }}>{error}</div>
            <button onClick={onClose} style={{ marginTop: 8, background: 'none', border: '1px solid #dc2626', color: '#dc2626', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 11 }}>Close</button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td colSpan={8} style={{ padding: 0 }}>
        <div style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', padding: '16px 20px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ ...mono, fontWeight: 700, fontSize: 16, color: '#1e293b' }}>{ticker}</span>
              {latest && (
                <span style={{ ...mono, fontSize: 13, color: latest.close >= latest.open ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                  {fmt$(latest.close)}
                </span>
              )}
              {latest?.rsi != null && (
                <span style={{
                  ...mono, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                  background: latest.rsi >= 70 ? '#fef2f2' : latest.rsi <= 30 ? '#f0fdf4' : '#f8fafc',
                  color: latest.rsi >= 70 ? '#dc2626' : latest.rsi <= 30 ? '#16a34a' : '#64748b',
                  border: `1px solid ${latest.rsi >= 70 ? '#fca5a5' : latest.rsi <= 30 ? '#86efac' : '#e2e8f0'}`
                }}>
                  RSI {latest.rsi.toFixed(0)}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Range selector */}
              {RANGE_OPTIONS.map(r => (
                <button key={r.label} onClick={() => setRange(r.label)}
                  style={{
                    ...mono, fontSize: 10, fontWeight: 600, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                    border: range === r.label ? `1px solid ${strategyColor}` : '1px solid #e2e8f0',
                    background: range === r.label ? `${strategyColor}15` : '#fff',
                    color: range === r.label ? strategyColor : '#94a3b8',
                  }}>
                  {r.label}
                </button>
              ))}
              <button onClick={onClose}
                style={{ ...mono, fontSize: 14, background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', marginLeft: 8 }}>
                ✕
              </button>
            </div>
          </div>

          {/* SMA toggles */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            {[
              { key: 'sma14', label: 'SMA 14', color: '#60a5fa' },
              { key: 'sma50', label: 'SMA 50', color: '#f59e0b' },
              { key: 'sma200', label: 'SMA 200', color: '#a78bfa' },
            ].map(({ key, label, color }) => (
              <button key={key} onClick={() => toggleSMA(key)}
                style={{
                  ...mono, fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                  border: `1px solid ${smaVisible[key] ? color : '#e2e8f0'}`,
                  background: smaVisible[key] ? `${color}20` : '#fff',
                  color: smaVisible[key] ? color : '#cbd5e1',
                  opacity: smaVisible[key] ? 1 : 0.6,
                }}>
                ● {label}{latest && latest[key] ? `: ${fmt$(latest[key])}` : ''}
              </button>
            ))}
          </div>

          {/* Price + SMA Chart with Volume overlay */}
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '12px 8px 4px', marginBottom: 12 }}>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={displayData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 9, fill: '#94a3b8', ...mono }}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  interval={Math.max(Math.floor(displayData.length / 8), 1)}
                />
                <YAxis
                  yAxisId="price"
                  orientation="right"
                  tick={{ fontSize: 9, fill: '#94a3b8', ...mono }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `$${v}`}
                  domain={['auto', 'auto']}
                />
                <YAxis
                  yAxisId="volume"
                  orientation="left"
                  tick={false}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, (max) => max * 4]}
                />
                <Tooltip content={<PriceTooltip />} />
                {/* Volume bars (subtle, behind price) */}
                <Bar yAxisId="volume" dataKey="volume" fill={`${strategyColor}25`} radius={[1, 1, 0, 0]} isAnimationActive={false} />
                {/* Price line */}
                <Line yAxisId="price" type="monotone" dataKey="close" stroke={strategyColor} strokeWidth={2} dot={false} isAnimationActive={false} />
                {/* SMA lines */}
                {smaVisible.sma14 && <Line yAxisId="price" type="monotone" dataKey="sma14" stroke="#60a5fa" strokeWidth={1.5} strokeDasharray="4 2" dot={false} isAnimationActive={false} />}
                {smaVisible.sma50 && <Line yAxisId="price" type="monotone" dataKey="sma50" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="6 3" dot={false} isAnimationActive={false} />}
                {smaVisible.sma200 && <Line yAxisId="price" type="monotone" dataKey="sma200" stroke="#a78bfa" strokeWidth={1.5} dot={false} isAnimationActive={false} />}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* RSI Chart */}
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '8px 8px 4px' }}>
            <div style={{ ...mono, fontSize: 10, fontWeight: 600, color: '#94a3b8', padding: '0 8px 4px', letterSpacing: 0.5 }}>RSI (14)</div>
            <ResponsiveContainer width="100%" height={100}>
              <ComposedChart data={displayData} margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={false} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                <YAxis
                  domain={[0, 100]}
                  ticks={[30, 50, 70]}
                  tick={{ fontSize: 9, fill: '#94a3b8', ...mono }}
                  tickLine={false}
                  axisLine={false}
                  orientation="right"
                />
                <Tooltip content={<RSITooltip />} />
                {/* Overbought/Oversold zones */}
                <ReferenceLine y={70} stroke="#fca5a5" strokeDasharray="3 3" strokeWidth={1} />
                <ReferenceLine y={30} stroke="#86efac" strokeDasharray="3 3" strokeWidth={1} />
                {/* RSI area fill */}
                <Area type="monotone" dataKey="rsi" stroke="none" fill={`${strategyColor}15`} isAnimationActive={false} />
                {/* RSI line */}
                <Line type="monotone" dataKey="rsi" stroke={strategyColor} strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </td>
    </tr>
  );
}
